import React, {useState, useEffect} from 'react'
import moment from 'moment'
import SearchResident from '../SearchResident'
import SelectInput from '../SelectInput'
import DateInput from './../DateInput'
import {Grid, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {H4HeaderWithoutBold} from '../../assets/styles/typography'
import {rxrRedColor} from '../../assets/styles/color'
import {spaceMedium} from '../../assets/styles/spacing'
import MultilineTextInput from '../MultilineTextInput'
import {useSelector} from 'react-redux'
import PropTypes from 'prop-types'
import {RXRButton} from '../RXRButton'
import Constant from './AmenitiesConstants'
import DiscardChangesDialog from '../DiscardChangesDialog'
import RXRIcon from '../RXRIcon'
import ConfirmDeleteDialog from '../ConfirmDeleteDialog'
import {reservationIsPast, reservationIsUpcoming, reservationNeedsApproval} from './ViewAmenityReservationsPage'
import useAmenityReservationScheduling from './useAmenityReservationScheduling'
import TextInput from '../TextInput'
import ToggleButtons from '../ToggleButtons'
import AmenityReservationStatusComponent from './AmenityReservationStatusComponent'
import SelectPaymentMethodsForResident from '../Services/SelectPaymentMethodsForResident'
import SimpleSpinner from '../SimpleSpinner'

import {chunkAvailabilityTimeRanges} from './amenityCalendarHelpers'
import {
  AMENITY_RESERVATION_USE_CASE_MOVE_IN,
  AMENITY_RESERVATION_USE_CASE_MOVE_OUT,
  AMENITY_RESERVATION_USE_CASE_STAFF,
  AMENITY_RESERVATION_USE_CASE_UNIT_TRANSFER,
  AMENITY_RESERVATION_USE_CASE_DELIVERY,
  ONBOARDING_AMENITY_KEY_PICKUP_LABEL,
} from '../../constants/ModelConstants'
import {getPayment} from '../../graphql/queries'
import QueryHelper from '../../lib/QueryHelper'

const {PAYMENT_STATE_PAID, PAYMENT_STATE_PROCESSING, PAYMENT_STATE_DECLINED, PAYMENT_STATE_CANCELLED, PAYMENT_STATE_REFUNDED} = Constant

const useStyles = makeStyles({
  container: {
    flexGrow: 1,
    marginBottom: 22,
  },
  userTypeToggleContainer: {
    width: 240,
  },
  errorText: {
    marginTop: spaceMedium,
    color: rxrRedColor,
  },
  statusContainer: {
    marginBottom: spaceMedium,
  },
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: `${spaceMedium}px 0`,
  },
  boldText: {
    fontWeight: 'bold',
  },
  ctaContainer: {
    marginTop: spaceMedium,
    display: 'flex',
    gap: spaceMedium,
  },
})

const keyPickupDisclaimer = `Key pick-up is available starting from the lease start date`

function CompactAmenityReservationEditor(props) {
  const classes = useStyles()
  const [showDenyRequestDialog, setShowDenyRequestDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [timeRanges, setTimeRanges] = useState([])
  const [selectedTimeRange, setSelectedTimeRange] = useState(null)
  const [depositPayment, setDepositPayment] = useState(null)
  const [isLoadingDepositPayment, setIsLoadingDepositPayment] = useState(false)

  const amenitiesLookup = useSelector(state => state.Amenities.amenitiesLookup)

  const {
    isEditMode,
    form,
    invalidItems,
    setForm,
    focusedReservation,
    formChanged,
    amenityData,
    isLoadingCalendar,
    isSaving,
    saveError,
    saveOrApprove,
    makeReservation,
    makeReservationAndApprove,
    handleReservationDate,
    loadMonthData,
    shouldDisableDate,
    deleteRequest,
    isFormComplete,
    updateRequest,
    resetForm,
    reservationDate,
    isLoadingAmenityDetails,
    isPlacingStaffReservation,
    setIsPlacingStaffReservation,
    chatResident,
    denyRequest,
    shouldShowSaveAndApproveButton,
  } = useAmenityReservationScheduling(props, (_, amenityCalendarDetail) => {
    const timeRangeChunks = chunkAvailabilityTimeRanges(
      amenityCalendarDetail.availability,
      amenityCalendarDetail.generalAvailability,
      amenityCalendarDetail.minReservationDuration,
      amenityCalendarDetail.minReservationDuration,
    )
    const timeRangeOptions = timeRangeChunks.map((range, index) => ({
      value: '' + index,
      label: `${moment(range.start).format('hh:mm A')} - ${moment(range.end).format('hh:mm A')}`,
    }))

    setTimeRanges(timeRangeOptions)
  })

  // Set selectedTimeRange when editing and timeRanges are loaded
  useEffect(() => {
    if (isEditMode && Array.isArray(timeRanges) && timeRanges.length > 0 && form.startAt && form.endAt) {
      const startStr = moment(form.startAt).format('hh:mm A')
      const endStr = moment(form.endAt).format('hh:mm A')
      const idx = timeRanges.findIndex(tr => {
        if (!tr.label) return false
        const [trStart, trEnd] = tr.label.split(' - ')
        return trStart === startStr && trEnd === endStr
      })
      if (idx !== -1 && idx !== selectedTimeRange) {
        setSelectedTimeRange(String(idx))
      }
    }
  }, [isEditMode, timeRanges, form.startAt, form.endAt])

  // Clear selectedTimeRange when reservationDate changes
  useEffect(() => {
    setSelectedTimeRange(null)
  }, [reservationDate])
  const selectedAmenity = amenitiesLookup && form.amenityId ? amenitiesLookup[form.amenityId] : null
  const isKeyPickupAmenity =
    selectedAmenity && selectedAmenity.isOnboardingOnly && selectedAmenity.label === ONBOARDING_AMENITY_KEY_PICKUP_LABEL
  const leaseFromDate = (form.occupancy && form.occupancy.leaseFromDate) || (form.occupancyData && form.occupancyData.leaseFromDate)
  const leaseFromDateMoment = leaseFromDate ? moment(leaseFromDate).startOf('day') : null
  const keyPickupShouldDisableDate = date => {
    if (isKeyPickupAmenity && leaseFromDateMoment) {
      return moment(date).startOf('day').isSameOrAfter(leaseFromDateMoment)
    }
    return shouldDisableDate(date)
  }

  const depositPaymentId = focusedReservation && focusedReservation.depositPaymentId
  const depositCost =
    (isEditMode ? (focusedReservation && focusedReservation.depositCost) || 0 : (amenityData && amenityData.depositPrice) || 0) > 0

  useEffect(() => {
    if (!depositPaymentId) {
      setDepositPayment(null)
      return
    }

    const fetchPayment = async () => {
      setIsLoadingDepositPayment(true)
      try {
        const paymentData = await QueryHelper.Instance().queryOnce(getPayment, {id: depositPaymentId})
        setDepositPayment(paymentData)
      } catch (error) {
        console.error('Error fetching deposit payment', error)
        setDepositPayment(null) // or handle error state
      } finally {
        setIsLoadingDepositPayment(false)
      }
    }

    fetchPayment()
  }, [depositPaymentId])

  const getDepositStatusText = () => {
    if (isLoadingDepositPayment) {
      return 'Deposit - loading...'
    }
    if (!depositPayment) {
      return 'Deposit - not yet paid'
    }

    switch (depositPayment.status) {
      case PAYMENT_STATE_PAID:
        return 'Deposit - paid'
      case PAYMENT_STATE_PROCESSING:
        return 'Deposit - processing'
      case PAYMENT_STATE_DECLINED:
        return 'Deposit - declined'
      case PAYMENT_STATE_CANCELLED:
        return 'Deposit - cancelled'
      case PAYMENT_STATE_REFUNDED:
        return 'Deposit - refunded'
      default:
        return 'Deposit - unknown status'
    }
  }

  const handleSelectTimeRange = indexOfSelectedTimeRange => {
    // Extract the start and end times from the selected time range
    const timeRange = timeRanges[parseInt(indexOfSelectedTimeRange)]
    const [startTimeString, endTimeString] = timeRange.label.split(' - ')

    // Create a moment object from the reservationDate string
    const reservationDateMoment = moment(reservationDate)

    // Combine the reservation date with the start and end times and convert to ISO strings
    const startAt = reservationDateMoment
      .clone()
      .hour(moment(startTimeString, 'hh:mm A').hour())
      .minute(moment(startTimeString, 'hh:mm A').minute())
      .toDate()
    const endAt = reservationDateMoment
      .clone()
      .hour(moment(endTimeString, 'hh:mm A').hour())
      .minute(moment(endTimeString, 'hh:mm A').minute())
      .toDate()

    // Update the form
    setForm({startAt, endAt})

    // Update the time range state variable
    setSelectedTimeRange(indexOfSelectedTimeRange)
  }

  const filteredAmenities =
    typeof props.amenitiesFilter === 'function'
      ? Object.values(amenitiesLookup).filter(props.amenitiesFilter)
      : Object.values(amenitiesLookup)
  const amenitiesOptions = filteredAmenities.map(a => ({
    value: a.id,
    label: a.label,
    isDisabled: isPlacingStaffReservation && a.label === ONBOARDING_AMENITY_KEY_PICKUP_LABEL,
  }))
  const isReservationSystemControlled = !!focusedReservation && focusedReservation.isSystemControlled
  const isReservationPast = !!focusedReservation && reservationIsPast(focusedReservation)

  const isSelectTimeDisabled = !form.partySize || !reservationDate || isReservationSystemControlled || isReservationPast
  return (
    <React.Fragment>
      {isEditMode && form.reservationState && (
        <div className={classes.statusContainer}>
          <AmenityReservationStatusComponent state={form.reservationState} updatedAt={focusedReservation && focusedReservation.updatedAt} />
        </div>
      )}
      <Grid container spacing={3}>
        {isReservationSystemControlled && (
          <Grid item xs={12}>
            <Typography style={{...H4HeaderWithoutBold, color: rxrRedColor, marginBottom: spaceMedium}}>
              This reservation is controlled by the system and cannot be modified
            </Typography>
          </Grid>
        )}
        {depositCost > 0 && (
          <Grid item xs={12}>
            <H4HeaderWithoutBold>Price Breakdown</H4HeaderWithoutBold>
            <Typography className={classes.boldText}>{getDepositStatusText()}</Typography>
          </Grid>
        )}
        {!isEditMode ? (
          <Grid item xs={12}>
            <div className={classes.userTypeToggleContainer}>
              <ToggleButtons
                selected={isPlacingStaffReservation ? 1 : 0}
                buttonLabels={['Resident', 'Staff']}
                onChange={v => {
                  setIsPlacingStaffReservation(!!v)
                  setForm({useCase: v ? AMENITY_RESERVATION_USE_CASE_STAFF : undefined})
                }}
                inverseColors={true}
              />
            </div>
          </Grid>
        ) : null}
        {props.amenityId ? null : (
          <Grid item xs={12}>
            <SelectInput
              onChange={id => setForm({amenityId: id})}
              label={'Amenity'}
              isRequired={true}
              options={amenitiesOptions}
              value={form.amenityId}
              disabled={!!(isReservationSystemControlled || isEditMode || (props.initialValues && props.initialValues.amenityId))}
              error={invalidItems.includes('amenityId')}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          {!isPlacingStaffReservation ? (
            <SearchResident
              value={form.residentId}
              isRequired={true}
              disabled={isEditMode}
              onSelectResident={r => setForm({residentId: r ? r.id : null})}
              error={invalidItems.includes('residentId')}
              includeSisterBuildings={true}
            />
          ) : (
            <TextInput
              isRequired={true}
              error={invalidItems.includes('title')}
              label="Title"
              value={form.title}
              onChange={val => setForm({title: val})}
            />
          )}
        </Grid>

        {isLoadingAmenityDetails ? (
          <Grid item xs={12}>
            <div className={classes.spinnerContainer}>
              <SimpleSpinner />
            </div>
          </Grid>
        ) : (
          <>
            {amenityData && amenityData.requiresPayment && !isPlacingStaffReservation && (
              <Grid item xs={12}>
                <SelectPaymentMethodsForResident
                  residentId={form.residentId}
                  onSelect={pm => setForm({paymentMethodId: pm || null})}
                  paymentMethodId={form.paymentMethodId}
                  isDisabled={
                    !form.residentId ||
                    isReservationSystemControlled ||
                    isReservationPast ||
                    form.reservationState === Constant.STATE_DECLINED
                  }
                  hasError={invalidItems.includes('paymentMethodId')}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <MultilineTextInput
                label="Notes"
                placeholder=""
                rows={4}
                rowsMax={4}
                isRequired={false}
                maxLength={2000}
                onChange={val => setForm({notes: val})}
                isDisabled={isReservationSystemControlled || isReservationPast}
                value={form.notes}
                error={invalidItems.includes('notes')}
              />
            </Grid>
          </>
        )}

        {!isPlacingStaffReservation && (
          <Grid item xs={12}>
            <SelectInput
              label="Use case"
              options={[
                {label: 'Resident move in', value: AMENITY_RESERVATION_USE_CASE_MOVE_IN},
                {label: 'Resident move out', value: AMENITY_RESERVATION_USE_CASE_MOVE_OUT},
                {label: 'Unit transfer', value: AMENITY_RESERVATION_USE_CASE_UNIT_TRANSFER},
                {label: 'Delivery', value: AMENITY_RESERVATION_USE_CASE_DELIVERY},
              ]}
              onChange={uC => setForm({useCase: uC})}
              value={form.useCase}
              isRequired={true}
              error={invalidItems.includes('useCase')}
            />
          </Grid>
        )}

        <Grid item xs={6}>
          <DateInput
            onChange={handleReservationDate}
            onMonthChange={loadMonthData}
            shouldDisableDate={isKeyPickupAmenity ? keyPickupShouldDisableDate : shouldDisableDate}
            min={moment().startOf('day').toDate()}
            label={'Reservation date'}
            isRequired={true}
            value={reservationDate}
            disabled={isReservationSystemControlled || isReservationPast}
            error={invalidItems.includes('startAt')}
            isLoading={isLoadingAmenityDetails}
            helperText={isKeyPickupAmenity && !isPlacingStaffReservation ? keyPickupDisclaimer : ''}
          />
        </Grid>
        <Grid item xs={6}>
          {isReservationPast ? (
            <TextInput
              isDisabled={true}
              value={`${moment(form.startAt).format('hh:mm A')} - ${moment(form.endAt).format('hh:mm A')}`}
              label={'Time Range'}
            />
          ) : (
            <SelectInput
              label="Time Range"
              value={selectedTimeRange}
              options={timeRanges}
              isLoading={isLoadingCalendar}
              onChange={handleSelectTimeRange}
              isRequired={true}
              isDisabled={isSelectTimeDisabled}
            />
          )}
        </Grid>
        {saveError ? (
          <Grid item xs={12}>
            <div className={classes.errorText}>{`${saveError}`}</div>
          </Grid>
        ) : null}
      </Grid>
      <div className={classes.ctaContainer}>
        {/* Create mode CTA's */}
        {!props.amenityReservationId && (
          <React.Fragment>
            <RXRButton onClick={() => makeReservation(true, amenityData?.needsApproval)} isLoading={isSaving}>
              {Constant.BTN_SAVE}
            </RXRButton>
            {shouldShowSaveAndApproveButton() && (
              <RXRButton onClick={() => makeReservationAndApprove(true)} isLoading={isSaving} type={RXRButton.TYPE_PRIMARY}>
                {Constant.BTN_SAVE_AND_APPROVE}
              </RXRButton>
            )}
            <RXRButton type={RXRButton.TYPE_TEXT} onClick={resetForm} isLoading={isSaving}>
              {Constant.BTN_CANCEL}
            </RXRButton>
          </React.Fragment>
        )}
        {/* Edit mode needs approval CTA's */}
        {!!props.amenityReservationId && reservationNeedsApproval(form) && (
          <React.Fragment>
            <RXRButton onClick={() => saveOrApprove()} disabled={!isFormComplete(form)} isLoading={isSaving}>
              {formChanged ? Constant.BTN_SAVE_AND_APPROVE_REQUEST : Constant.BTN_APPROVE_REQUEST}
            </RXRButton>
            <RXRButton type={RXRButton.TYPE_DESTRUCTIVE} onClick={() => setShowDenyRequestDialog(true)} isLoading={isSaving}>
              {Constant.BTN_DENY_REQUEST}
            </RXRButton>
            <RXRButton type={RXRButton.TYPE_TEXT} onClick={resetForm} isLoading={isSaving}>
              {Constant.BTN_CANCEL}
            </RXRButton>
          </React.Fragment>
        )}
        {/* Edit mode past CTA's */}
        {!!props.amenityReservationId && reservationIsUpcoming(form) && (
          <React.Fragment>
            <RXRButton
              onClick={() => updateRequest()}
              disabled={!isFormComplete(form) || form.isSystemControlled || !formChanged}
              isLoading={isSaving}
            >
              {Constant.BTN_UPDATE_REQUEST}
            </RXRButton>
            {!isPlacingStaffReservation && <RXRButton onClick={() => chatResident()}>{Constant.BTN_CHAT_RESIDENT}</RXRButton>}
            <RXRButton
              type={RXRButton.TYPE_DESTRUCTIVE}
              onClick={() => setShowDeleteDialog(true)}
              disabled={form.isSystemControlled}
              isLoading={isSaving}
            >
              {Constant.BTN_DELETE}
            </RXRButton>
          </React.Fragment>
        )}
        {/* Edit mode past CTA's */}
        {isReservationPast && <RXRButton onClick={() => chatResident()}>{Constant.BTN_CHAT_RESIDENT}</RXRButton>}
      </div>
      <DiscardChangesDialog
        isOpen={showDenyRequestDialog}
        discardIcon={RXRIcon.NOT_ALLOWED}
        discardTitle={'Deny request'}
        discardMessage={'Are you sure you want to deny this request?'}
        discardButton={'Deny'}
        onCancel={() => {
          setShowDenyRequestDialog(false)
        }}
        onDiscard={async () => {
          await denyRequest()
        }}
      />
      <DiscardChangesDialog hasChanges={formChanged} onDiscard={resetForm} />
      <ConfirmDeleteDialog
        isOpen={showDeleteDialog}
        prompt={'Are you sure you want to delete this reservation?'}
        onConfirmDelete={deleteRequest}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </React.Fragment>
  )
}

CompactAmenityReservationEditor.propTypes = {
  onComplete: PropTypes.func.isRequired,
  amenityReservationId: PropTypes.string,
  initialValues: PropTypes.object,
  onUpdateForm: PropTypes.func,
  amenitiesFilter: PropTypes.func,
}

export default CompactAmenityReservationEditor
